import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base, ReverLoading, toast } from '@itsrever/design-system'
import { ShippingRule } from '@/application/manage-shipping'
import {
  useAvailablePublicShippingRules,
  useCopies,
  useOnboardEcommerce,
  useUnsavedChangesGuard
} from '@/hooks'
import { useCountries } from '@/application/queries'
import { OnboardingInfo } from '@/domain/onboarding'
import { useDialog } from '@/ui/components/dialog/dialog-provider'
import { newRuleKey } from '@/ui/components/rule-list'
import {
  Draft,
  RuleTable,
  countIncomplete,
  fromDraft
} from '@/ui/components/shipping-costs'

interface OnboardingShippingCostsProps {
  ecommerceId: string
  onSave: () => void
  onboardingInfo: OnboardingInfo
  urlParams: Record<string, string>
}

// A new ecommerce has nothing stored: every rule is new.
const STORED: ShippingRule[] = []

const firstDraft = (): Draft => ({
  rules: [
    {
      key: newRuleKey(),
      params: { cost: '0' },
      returnOriginalShippingCosts: false,
      conditions: [{ function: 'country', comparison: 'In', values: [] }]
    }
  ],
  fallback: { cost: '0', returnOriginalShippingCosts: false }
})

export const OnboardingShippingCosts: React.FC<
  OnboardingShippingCostsProps
> = ({ ecommerceId, onSave, onboardingInfo, urlParams }) => {
  const { t } = useTranslation()
  const { loadingDefaultCopies } = useCopies()
  const { countries, loadingCountries } = useCountries()
  const [draft, setDraft] = useState(firstDraft)

  const { availableConfiguration, loadingAvailableRules, reasonKeys } =
    useAvailablePublicShippingRules(countries ?? [])
  const { loadingNewShippingConfig, onboard } = useOnboardEcommerce(
    countries ?? [],
    ecommerceId,
    availableConfiguration,
    reasonKeys,
    onboardingInfo,
    urlParams
  )

  const incomplete = countIncomplete(draft, STORED)
  const showDialog = useDialog()
  const confirmLeave = useCallback(
    () =>
      showDialog({
        title: t('save_bar.leave_title'),
        message: t('save_bar.leave_message'),
        okButtonText: t('save_bar.leave_ok'),
        cancelButtonText: t('save_bar.cancel')
      }),
    [showDialog, t]
  )
  // Leaving drops the whole onboarding; warn while a rule is half built.
  useUnsavedChangesGuard(incomplete > 0, confirmLeave)

  const finish = async (rules: ShippingRule[]) => {
    if (await onboard(rules)) onSave()
  }

  const save = () => {
    if (incomplete > 0) {
      toast({ text: t('edit_shipping.warn_toast'), variant: 'warning' })
      return
    }
    finish(fromDraft(draft))
  }

  if (loadingAvailableRules || loadingCountries || loadingDefaultCopies)
    return <ReverLoading />

  return (
    <div className="flex flex-col gap-6 px-8 pb-4">
      <RuleTable
        draft={draft}
        stored={STORED}
        availableConfiguration={availableConfiguration}
        editable
        onChange={setDraft}
      />
      <div className="flex items-center justify-end gap-4">
        <Base.Button
          type="button"
          variant="ghost"
          disabled={loadingNewShippingConfig}
          onClick={() => finish([])}
        >
          {t('onboarding.skip_shipping_costs')}
        </Base.Button>
        <Base.Button
          type="button"
          variant="primary"
          loading={loadingNewShippingConfig}
          onClick={save}
        >
          {t('edit_shipping.save_changes')}
        </Base.Button>
      </div>
    </div>
  )
}
